<template>
  <div>
    <b-card>
      <h2 v-if="!readonly" v-translate>Search and Delete</h2>
      <b-row>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-name" v-translate>Quote Name</label>
            <input id="quote-name" v-model.lazy="filter.quoteName" type="text" class="mb-2 col-12 form-control">
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-from" v-translate>Area From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteAreaFrom" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-to" v-translate>Area To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteAreaTo" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-region-from" v-translate>Region From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteRegionFrom" :options="regionsSelect">
              <template #first>
                <option :value="null" v-translate>Select Region</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-region-to" v-translate>Region To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteRegionTo" :options="regionsSelect">
              <template #first>
                <option :value="null" v-translate>Select Region</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Size</label>
            <b-form-select id="quote-size" v-model="filter.quoteShippingSizeType" :options="this.sizeTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4" v-if="!readonly">
          <div class="form-group">
            <label for="quote-size" v-translate>Imported Quotes CSV</label>
            <b-form-file
              v-model="fileImported"
              :placeholder="$gettext('Choose an Imported CSV or drop it here...')"
              :drop-placeholder="$gettext('Drop file here...')"
              @input="handleFileImportedSelect"
              ref="importedQuotes"
              id="importedQuotes"
              accept=".csv"
            ></b-form-file>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Company</label>
            <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" v-if="filter.quoteCompanies.length === 0" />
            <div v-for="(item, index) in filter.quoteCompanies" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeCompanyFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <label for="quote-size">&nbsp;</label>
          <b-form-row>
            <b-col>
              <b-button size="sm" block variant="success" @click="searchQuotes" class="mb-2" :disabled="working">
                <b-spinner small v-if="working" />
                <translate>Search</translate>
              </b-button>
            </b-col>
            <b-col>
              <b-button size="sm" block variant="danger" @click="resetQuoteSearch" class="mb-2" :disabled="working">
                <translate>Reset Search</translate>
              </b-button>
            </b-col>
          </b-form-row>
        </b-col>
      </b-row>
      <div v-if="searchedQuotes.length && !readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="primary" @click="backupSelected" :disabled="selected.length === 0 || deleting" v-translate>
          Backup Selected
        </b-button>
        <b-button variant="warning" v-b-modal.modalEditFormulas :disabled="!backuped || deleting" v-translate>
          EDIT Selected
        </b-button>
        <b-button variant="danger" @click="deleteSelected" :disabled="!backuped || deleting" v-translate>
          DELETE Selected
        </b-button>
      </div>
      <div v-if="searchedQuotes.length && readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="warning" v-b-modal.modalEditFormulas v-translate>
          VIEW Selected
        </b-button>
      </div>
      <b-modal id="modalEditFormulas" size="xl" ref="modalEditFormulas" hide-footer @hidden="resetEditFormulas">
        <div slot="modal-title" v-translate>Bulk Edit Formulas</div>
        <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <b-row v-if="updating">
          <b-col>
            <b-progress :max="totalToUpdate.length" height="2rem" class="mb-3">
              <b-progress-bar :value="updatedCount">
                <span v-translate="{count: updatedCount, total: totalToUpdate.length}">Progress: <strong>%{count} / %{total}</strong></span>
              </b-progress-bar>
            </b-progress>
          </b-col>
        </b-row>
        <b-row v-if="!readonly">
          <b-col>
            <h4 v-translate>Search &amp; Replace in names</h4>
            <b-row>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="search-for" v-translate>Search for:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="search-for" v-model="searchFor"></b-form-input>
                  </b-col>
                </b-row>
              </b-col>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="replace-with" v-translate>Replace with:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="replace-with" v-model="replaceWith"></b-form-input>
                  </b-col>
                </b-row>
              </b-col>
              <b-col sm="4">
                <b-button @click="bulkReplaceInQuoteNames" :disabled="updating" v-translate>Replace</b-button>
              </b-col>
            </b-row>
            <hr>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <h4 v-translate>Formulas</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="formula-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="formula-by-percentage" v-model="formulaByPercentage" :disabled="formulasIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="formulasIsNan" v-model="formulasAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(formula, i) in distinctFormulas" :key="'formula' + i">
              <b-col sm="5">
                <label :for="`formula-${i}`">{{ formula }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`formula-${i}`" v-model="newFormulas[formula]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-button @click="bulkUpdateFormulas" :disabled="updating" v-if="!readonly" v-translate>Update</b-button>
      </b-modal>
      <b-alert variant="danger" :show="deleting">
        <span v-translate="{count: count, total: selected.length}">Deleting %{count}/%{total}</span>
      </b-alert>
      <p v-if="total" v-translate="{total: this.total}">Total: <strong>%{total}</strong></p>
      <b-table id="quoteProvidersTable" ref="quoteProvidersTable" @row-selected="rowSelected" :busy.sync="working" selectable stacked="md" foot-clone :items="searchedQuotes" :fields="quotesFields" v-if="searchedQuotes.length > 0">
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(reference)="data">
          <a :href="'/edit/quote-providers/' + data.item.id" target="_blank" v-if="!readonly">{{data.item.reference}}</a>
          <span v-else>{{data.item.reference}}</span>
        </template>
        <template v-slot:cell(size_id)="data">
          {{ data.item.size_id ? sizeTypes.findIndex(x => x.id === data.item.size_id) !== -1 ? sizeTypes.find(x => x.id === data.item.size_id).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_from)="data">
          {{ data.item.area_id_from ? areas.findIndex(x => x.id === data.item.area_id_from) !== -1 ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_to)="data">
          {{ data.item.area_id_to ? areas.findIndex(x => x.id === data.item.area_id_to) !== -1 ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(region_id_from)="data">
          {{ data.item.region_id_from ? regions.findIndex(x => x.id === data.item.region_id_from) !== -1 ? regions.find(x => x.id === data.item.region_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(region_id_to)="data">
          {{ data.item.region_id_to ? regions.findIndex(x => x.id === data.item.region_id_to) !== -1 ? regions.find(x => x.id === data.item.region_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(platform_id)="data">
          {{ data.item.platform_id ? platforms.find(x => x.id === data.item.platform_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(shipping_type_id)="data">
          {{ data.item.shipping_type_id ? shippingTypes.find(x => x.id === data.item.shipping_type_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(company_id)="row">
          <strong class="badge" :style="`background-color: ${stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id)};${textContrastColor(stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id))}`">{{row.item.company.name || row.item.company.id}}</strong>
        </template>
      </b-table>
    </b-card>
    <b-card v-if="!readonly">
      <h2 v-translate>Import</h2>
      <b-form-file
        v-model="file"
        :placeholder="$gettext('Choose a file or drop it here...')"
        :drop-placeholder="$gettext('Drop file here...')"
        @input="handleFileSelect"
        ref="csvToImport"
        id="csvToImport"
        accept=".csv"
      ></b-form-file>
      <b-alert variant="success" :show="importing" class="mt-3">
        <span v-translate="{count: count, total: importData.length}">Importing %{count}/%{total}</span>
      </b-alert>
      <b-alert variant="info" :show="importData.length > 0 && errors.length === 0" class="mt-3">
        <h5 v-translate="{total: importData.length}">It seems there are no errors and you are about to bulk import %{total} quotes. Is it OK?</h5>
        <b-button variant="success" @click="bulkImport" :disabled="importing">
          <b-spinner small v-show="importing" />
          <translate>OK! Let's import them!</translate>
        </b-button>
        <b-button variant="danger" @click="file = null; importData = []" :disabled="importing" v-translate>
          NOPE! Cancel it!
        </b-button>
      </b-alert>
      <b-alert variant="danger" :show="errors.length > 0" class="mt-3">
        <h5 v-translate>It seems there are errors, check 'em out:</h5>
        <ul>
          <li v-for="(item, index) in errors" :key="index">
            {{ item.message }}{{item.row ? ' (row: ' + item.row + (item.index ? ' - col: ' + item.index : '') + ')' : ''}}
          </li>
        </ul>
        <p v-translate>Please, review and fix your CSV file and upload it again</p>
      </b-alert>
    </b-card>
  </div>
</template>
<script>
  import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
  import Papa from 'papaparse'

  export default {
    name: 'BulkQuoteProvidersList',
    components: {
      CompanyAutocomplete
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        file: null,
        fileImported: null,
        importData: [],
        errors: [],
        quotesFields: [
          {key: 'reference', label: this.$gettext('Name')},
          {key: 'company_id', label: this.$gettext('Provider')},
          {key: 'area_id_from', label: this.$gettext('Area From')},
          {key: 'area_id_to', label: this.$gettext('Area To')},
          {key: 'region_id_from', label: this.$gettext('Region From')},
          {key: 'region_id_to', label: this.$gettext('Region To')},
          {key: 'size_id', label: this.$gettext('Size')},
          {key: 'formula', label: this.$gettext('Formula')},
          {key: 'date_from', label: this.$gettext('From Date')},
          {key: 'date_to', label: this.$gettext('To Date')}
        ],
        filters: [],
        filter: {
          ids: [],
          quoteName: '',
          quoteIncludes: null,
          quoteAreaFrom: null,
          quoteAreaTo: null,
          quoteRegionFrom: null,
          quoteRegionTo: null,
          quoteCompanies: [],
          quoteShippingSizeType: ''
        },
        working: false,
        searchedQuotes: [],
        companiesQuotes: [],
        companyAcOptions: {
          debounce: 500,
          placeholder: this.$gettext('Company Name, Business Name or TIN'),
          inputClass: 'form-control'
        },
        searchCompanyQuery: '',
        minCharacter: 3,
        areas: [],
        platforms: [],
        platformsSelect: [],
        shippingTypes: [],
        shippingTypesSelect: [],
        sizeTypes: [],
        sizeTypesSelect: [],
        selected: [],
        selectAllTrigger: false,
        backuped: false,
        importing: false,
        deleting: false,
        count: 0,
        batchUpdateChunk: 15,
        total: null,
        updating: false,
        totalToUpdate: 0,
        updatedCount: 0,
        formulaByPercentage: null,
        newFormulas: {},
        formulasAsIntegers: false,
        searchFor: null,
        replaceWith: null,
        createdAtFrom: null,
        ids: []
      }
    },
    computed: {
      distinctFormulas() {
        return [...new Set(this.selected.map(row => row.formula))]
      },
      formulasIsNan() {
        return !!this.distinctFormulas.filter(x => isNaN(x))[0]
      }
    },
    watch: {
      formulaByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctFormulas.length; i++) {
            this.newFormulas[this.distinctFormulas[i]] = parseFloat(this.distinctFormulas[i]) + parseFloat(this.distinctFormulas[i]) * val / 100
          }
        } else {
          this.newFormulas = {}
        }
      },
      'filter.ids': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteIds'] = [ 'id', 'in', val ]
            } else {
              this.filters['quoteIds'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteName': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteName'] = [ 'reference', 'ct', val ]
            } else {
              this.filters['quoteName'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteAreaFrom'] = [ 'area_id_from', 'eq', val ]
            } else {
              this.filters['quoteAreaFrom'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteAreaTo'] = [ 'area_id_to', 'eq', val ]
            } else {
              this.filters['quoteAreaTo'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteRegionFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteRegionFrom'] = [ 'region_id_from', 'eq', val ]
            } else {
              this.filters['quoteRegionFrom'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteRegionTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteRegionTo'] = [ 'region_id_to', 'eq', val ]
            } else {
              this.filters['quoteRegionTo'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingSizeType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteShippingSizeType'] = [ 'size_id', 'eq', val ]
            } else {
              this.filters['quoteShippingSizeType'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteCompanies': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              let companies = val.map(function (item) {
                return item.id
              })
              this.filters['quoteCompanies'] = [ 'company_id', 'in', companies ]
            } else {
              this.filters['quoteCompanies'] = []
            }
          }
        },
        deep: true
      },
      selectAllTrigger(val) {
        if (val === true) {
          this.selectAll()
        } else {
          this.unselectAll()
        }
      },
      selected(val) {
        console.log(val)
      }
    },
    created() {
      this.fetchAreas()
      this.fetchRegions()
      this.$http.get(this.$appConfig.api.baseUrl + 'sizes')
        .then(response => {
          this.sizeTypes = response.body.data
          this.sizeTypesSelect = this.sizeTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.sizeTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific size')
          })
          this.sizeTypes.unshift({
            id: '',
            name: this.$gettext('All sizes')
          })
          for (let i = 0; i < this.sizeTypesSelect.length; i++) {
            for (let n = 0; n < this.companyFormulas.length; n++) {
              if (this.companyFormulas[n].size_type_id === this.sizeTypesSelect[i].value) {
                this.sizeTypesSelect.splice(i, 1)
              }
            }
          }
        }).catch(response => {

        })
    },
    methods: {
      resetEditFormulas() {
        this.newFormulas = {}
        this.formulaByPercentage = null
        this.searchFor = null
        this.replaceWith = null
      },
      async bulkReplaceInQuoteNames() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => x.reference.indexOf(this.searchFor) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          console.log(this.selected[i])

          let payload = {}
          if (this.selected[i].reference.indexOf(this.searchFor) !== -1) {
            payload.reference = this.selected[i].reference.replace(this.searchFor, this.replaceWith)
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quote-providers/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.filter.quoteName = this.replaceWith
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditFormulas.hide()
      },
      async bulkUpdateFormulas() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => Object.keys(this.newFormulas).indexOf(x.formula) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          let payload = {}
          if (this.newFormulas[this.selected[i].formula]) {
            payload.formula = this.newFormulas[this.selected[i].formula]
            if (this.formulasAsIntegers && !isNaN(payload.formula)) {
              payload.formula = parseInt(payload.formula)
            }
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quote-providers/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditFormulas.hide()
      },
      handleFileImportedSelect(evt) {
        let file = this.fileImported
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: function (results) {
              _this.filter.ids = results.data.map(id => { return id.quote_id })
            }
          })
        }
      },
      handleFileSelect(evt) {
        let file = this.file
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: function (results) {
              _this.errors = []
              _this.importData = []
              if (results.errors.length === 0) {
                if (
                  results.meta.fields.indexOf('reference') !== -1 &&
                  (results.meta.fields.indexOf('area_id_from') !== -1 &&
                  results.meta.fields.indexOf('area_id_to') !== -1) ||
                  (results.meta.fields.indexOf('region_id_from') !== -1 &&
                  results.meta.fields.indexOf('region_id_to') !== -1) &&
                  results.meta.fields.indexOf('company_id') !== -1 &&
                  results.meta.fields.indexOf('formula') !== -1
                ) {
                  _this.importData = results.data
                  // console.log((_this.importData[1].companies + '').split(','))
                } else {
                  _this.errors = [
                    {
                      message: this.$gettext('There are one or more missing required data columns')
                    }
                  ]
                }
              } else {
                _this.errors = results.errors
                console.log(results.errors)
              }
            }
          })
        }
      },
      async bulkImport() {
        this.importing = true
        this.count = 0
        this.ids = []
        const chunkSize = 40
        let completeData = [...new Set(this.importData)]
        const chunks = [...Array(Math.ceil(this.importData.length / chunkSize))].map(_ => completeData.splice(0, chunkSize))
        for (let i = 0; i < chunks.length; i++) {
          await this.importChunk(chunks[i])
        }
        this.importing = false
        let filename = 'imported_quote_providers__' + this.createdAtFrom + '.csv'
        this.exportToCsv(filename, Papa.unparse(this.ids))
        this.filters.createdAtFrom = ['created_at', 'gte', this.moment.utc(this.createdAtFrom).subtract(15, 'seconds').format('YYYY-MM-DD HH:mm:ss')]
        this.filters.createdAtTo = ['created_at', 'lte', this.moment().utc().add(5, 'seconds').format('YYYY-MM-DD HH:mm:ss')]
        this.searchQuotes()
      },
      async importChunk(chunk) {
        let toPost = []
        for (let i = 0; i < chunk.length; i++) {
          // let quote = JSON.parse(JSON.stringify(chunk[i]).replace(/{([a-zA-Z]+):[^}]+}/g, '{$1}'))

          let autodiscover = this.customValuesAutodiscover(chunk[i].formula)
          let cleanedUpData = JSON.parse(JSON.stringify(chunk[i]).replace(/{([a-zA-Z]+):[^}]+}/g, '{$1}'))
          let quote = {...cleanedUpData, ...autodiscover}

          let paramsQuotes = {
            reference: quote.reference,
            country_iso_code: quote.country_iso_code,
            area_id_from: quote.area_id_from || null,
            area_id_to: quote.area_id_to || null,
            region_id_from: quote.region_id_from || null,
            region_id_to: quote.region_id_to || null,
            size_id: quote.size_id,
            company_id: quote.company_id,
            formula: quote.formula,
            formula_params: quote.formula_params,
            date_from: quote.date_from,
            date_to: quote.date_to
          }
          if (quote.area_id_from && quote.area_id_to) {
            delete paramsQuotes.region_id_from
            delete paramsQuotes.region_id_to
          } else if (quote.region_id_from && quote.region_id_to) {
            delete paramsQuotes.area_id_from
            delete paramsQuotes.area_id_to
          }
          toPost.push(this.$http.post(this.$appConfig.api.baseUrl + 'quote-providers', paramsQuotes))
        }
        await Promise.all(toPost)
          .then(responses => {
            for (let j = 0; j < responses.length; j++) {
              if (!this.createdAtFrom) {
                this.createdAtFrom = responses[j].body.created_at
              }

              this.ids.push({quote_id: responses[j].body.id})
              this.count = this.count + 1
            }
          })
      },
      customValuesAutodiscover(formulas) {
        const excludeValues = [
          'kgms'
        ]
        const regexp = /\{([^}]+)\}/g
        let valuesArray = []
        let paramsArray = []
        let match = regexp.exec(formulas)
        while (match !== null) {
          if (!excludeValues.includes(match[1])) {
            valuesArray.push(match[1])
          }
          if (!excludeValues.includes(match[1])) {
            paramsArray.push(match[1])
          }
          match = regexp.exec(formulas)
        }
        if (paramsArray.length) {
          return {
            formula_params: Object.fromEntries(paramsArray.map(x => x.split(':')))
          }
        } else {
          return null
        }
      },
      async searchQuotes() {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        let params = {
          page: 0,
          limit: 500,
          sort: [
            {
              key: 'reference',
              direction: 'asc'
            }
          ],
          includes: [
            'company',
            'area_from',
            'area_to'
          ],
          filter_groups: []
        }
        if (!this.$cookies.get('bypassCountry')) {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models['quote-providers'].countrize, 'eq', window.country]
              ]
            }
          )
        }

        if (this.filters) {
          params.filter_groups.push(
            {filters: Object.values(this.filters)}
          )
        }
        await this.doSearchQuotes(params)
        this.working = false
      },
      async doSearchQuotes(params, prevQuotes = []) {
        let searchedQuotes = []
        return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quote-providers'].endpoint, {params})
          .then(async response => {
            searchedQuotes = response.body.data

            searchedQuotes = prevQuotes.concat(searchedQuotes)
            if (response.body.data.length !== 0) {
              params.page = params.page + 1
              await this.doSearchQuotes(params, searchedQuotes)
            } else {
              this.total = searchedQuotes.length
              this.searchedQuotes = searchedQuotes
            }
          })
      },
      resetQuoteSearch() {
        this.total = null
        this.resetting = true
        this.searchedQuotes = []
        this.filters = []
        this.fileImported = null
        Object.assign(this.$data.filter, this.$options.data.call(this).filter)
        setTimeout(function () {
          this.resetting = false
        }.bind(this), 500)
      },
      rowSelected(items) {
        this.selected = items
      },
      selectAll() {
        let quoteProvidersTable = this.$refs.quoteProvidersTable.$el
        let tableBody = quoteProvidersTable.getElementsByTagName('tbody')[0]
        let tableRows = tableBody.getElementsByTagName('tr')
        this.unselectAll()
        for (const row of tableRows) {
          row.click()
        }
      },
      unselectAll() {
        this.$refs.quoteProvidersTable.clearSelected()
      },
      onSearchCompanySelected(item) {
        this.filter.quoteCompanies.push({id: item.id, name: item.name})
        this.$refs.companyAc.searchQuery = ''
      },
      removeCompanyFromFilters(index) {
        this.filter.quoteCompanies.splice(index, 1)
      },
      backupSelected() {
        let quotesExport = []
        for (let i = 0; i < this.selected.length; i++) {
          let originalQuote = JSON.parse(JSON.stringify(this.selected[i]))

          try {
            let theQuote = originalQuote
            delete (theQuote.id)
            delete (theQuote.applied_by_id)
            delete (theQuote.created_at)
            delete (theQuote.updated_at)
            delete (theQuote.deleted_at)
            delete (theQuote.company)
            delete (theQuote.area_from)
            delete (theQuote.area_to)
            quotesExport.push(theQuote)
          } catch (e) {
            console.log(e)
          }
        }

        let filename = 'quote_providers_backup'
        Object.values(this.filters).forEach(filter => {
          if (filter.length > 0) {
            filename += '__' + filter.join('-')
          }
        })
        filename += '.csv'
        this.exportToCsv(filename, Papa.unparse(quotesExport))
        this.backuped = true
      },
      async deleteSelected() {
        if (
          confirm(this.$gettext('Are you sure you want to DELETE the selected rows?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))
        ) {
          this.deleting = true
          this.count = 0
          const chunkSize = 40
          let selected = [...new Set(this.selected)]
          const chunks = [...Array(Math.ceil(this.selected.length / chunkSize))].map(_ => selected.splice(0, chunkSize))
          for (let i = 0; i < chunks.length; i++) {
            await this.deleteChunk(chunks[i])
          }
          this.deleting = false
          this.$router.push({ name: 'modelList', params: { type: 'quote-providers' }, query: { deleted: true } })
        }
      },
      async deleteChunk(chunk) {
        let toDelete = []
        for (let i = 0; i < chunk.length; i++) {
          let theQuote = chunk[i]
          toDelete.push(this.$http.delete(this.$appConfig.api.baseUrl + 'quote-providers/' + theQuote.id))
        }
        return await Promise.all(toDelete)
          .then(responses => {
            toDelete = []
            this.count = this.count + responses.length
          })
      },
      exportToCsv(filename, csv) {
        let blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        if (navigator.msSaveBlob) { // IE 10+
          navigator.msSaveBlob(blob, filename)
        } else {
          let link = document.createElement('a')
          if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            let url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      }
    }
  }
</script>
